import React, { useState } from "react";
import { motion } from "framer-motion";
import { PaperPlaneTilt, Megaphone, ArrowsClockwise } from "@phosphor-icons/react";
import { toast } from "sonner";
import api from "../lib/api";

const AUDIENCES = [
  { id: "active", label: "All active (paid + trial)", color: "#22C55E" },
  { id: "paid", label: "Paid subscribers only", color: "#007AFF" },
  { id: "trial", label: "Free trial users only", color: "#E0B400" },
  { id: "all", label: "Everyone (incl. canceled)", color: "#FF3B30" },
];

export default function AdminBroadcast() {
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [audience, setAudience] = useState("active");
  const [sending, setSending] = useState(false);
  const [lastResult, setLastResult] = useState(null);

  const send = async () => {
    if (subject.trim().length < 2 || message.trim().length < 2) {
      toast.error("Subject and message required.");
      return;
    }
    const audLabel = AUDIENCES.find((a) => a.id === audience)?.label || audience;
    if (!window.confirm(`Send this message to ${audLabel}?\n\nThis action cannot be undone.`)) return;
    setSending(true);
    try {
      const { data } = await api.post("/admin/broadcast", {
        subject: subject.trim(),
        message: message.trim(),
        audience,
      });
      setLastResult(data);
      toast.success(`Sent to ${data.sent} of ${data.candidates} candidates.`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Broadcast failed");
    } finally {
      setSending(false);
    }
  };

  const sendDailyPicks = async () => {
    if (!window.confirm("Re-send today's locked picks email to all active subscribers? Recipients who already received today's email will be skipped.")) return;
    setSending(true);
    try {
      const { data } = await api.post("/admin/picks/send-daily-email");
      toast.success(`Daily picks email: ${data.sent} sent · ${data.skipped} skipped · ${data.picks_count} picks for ${data.date}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Daily picks send failed");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="max-w-[1000px] mx-auto px-6 py-10" data-testid="admin-broadcast-page">
      <div className="mb-8">
        <div className="text-[11px] uppercase tracking-[0.3em] text-[#007AFF] mb-1">Admin · Outreach</div>
        <h1 className="font-display text-5xl sm:text-6xl leading-none">BROADCAST.</h1>
        <p className="text-sm text-zinc-400 mt-3 max-w-2xl">
          One-shot email to any segment of your users. Daily picks go out automatically every morning at 9 AM ET to all active subscribers — use that button below if you need to re-fire or send a mid-day update.
        </p>
      </div>

      {/* Daily picks shortcut */}
      <motion.div
        initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }}
        className="bg-[#121212] border border-[#22C55E]/30 rounded-sm p-5 mb-8 flex flex-wrap items-center justify-between gap-3"
        data-testid="daily-picks-shortcut"
      >
        <div>
          <div className="text-[10px] uppercase tracking-[0.3em] text-[#22C55E] mb-1">Daily picks email</div>
          <div className="text-sm text-zinc-300">Fires automatically at 9:00 AM ET every morning.</div>
        </div>
        <button
          onClick={sendDailyPicks}
          disabled={sending}
          data-testid="send-daily-picks-now"
          className="inline-flex items-center gap-2 bg-[#22C55E] hover:bg-[#16A34A] disabled:opacity-50 text-black font-bold uppercase tracking-[0.2em] text-xs px-5 py-2.5 rounded-sm transition-colors"
        >
          <ArrowsClockwise size={14} weight="bold" />
          {sending ? "Sending…" : "Send daily picks now"}
        </button>
      </motion.div>

      {/* Broadcast form */}
      <div className="bg-[#121212] border border-white/10 rounded-sm p-6 space-y-5" data-testid="broadcast-form">
        <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.3em] text-[#007AFF]">
          <Megaphone size={14} weight="fill" /> Custom message
        </div>

        <div>
          <label className="block text-[10px] uppercase tracking-widest text-zinc-400 mb-1.5">Audience</label>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-2">
            {AUDIENCES.map((a) => (
              <button
                key={a.id}
                onClick={() => setAudience(a.id)}
                data-testid={`audience-${a.id}`}
                className={`text-left px-3 py-2.5 border rounded-sm text-xs transition-colors ${audience === a.id ? "border-white bg-white/5" : "border-white/10 hover:border-white/30"}`}
              >
                <div className="font-bold text-white" style={{ color: audience === a.id ? a.color : undefined }}>
                  {a.label}
                </div>
              </button>
            ))}
          </div>
        </div>

        <div>
          <label className="block text-[10px] uppercase tracking-widest text-zinc-400 mb-1.5" htmlFor="bc-subject">Subject</label>
          <input
            id="bc-subject"
            data-testid="broadcast-subject"
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            maxLength={200}
            placeholder="A quick update from the team…"
            className="w-full bg-[#0A0A0A] border border-white/15 hover:border-white/30 focus:border-[#007AFF] outline-none rounded-sm px-3 py-2.5 text-sm font-mono-d transition-colors"
          />
        </div>

        <div>
          <label className="block text-[10px] uppercase tracking-widest text-zinc-400 mb-1.5" htmlFor="bc-message">Message</label>
          <textarea
            id="bc-message"
            data-testid="broadcast-message"
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            rows={8}
            maxLength={20000}
            placeholder="Write your message in plain text. Line breaks are preserved."
            className="w-full bg-[#0A0A0A] border border-white/15 hover:border-white/30 focus:border-[#007AFF] outline-none rounded-sm px-3 py-2.5 text-sm font-mono-d transition-colors resize-y"
          />
          <div className="text-[10px] text-zinc-600 mt-1 text-right font-mono-d">{message.length}/20000</div>
        </div>

        <button
          onClick={send}
          disabled={sending}
          data-testid="broadcast-submit"
          className="w-full inline-flex items-center justify-center gap-2 px-6 py-3 bg-[#007AFF] hover:bg-[#0064D6] disabled:opacity-50 text-white text-xs uppercase tracking-[0.25em] font-bold rounded-sm transition-colors"
        >
          <PaperPlaneTilt size={14} weight="fill" />
          {sending ? "Sending…" : `Send to ${AUDIENCES.find((a) => a.id === audience)?.label.toLowerCase()}`}
        </button>

        {lastResult && (
          <div className="border border-[#22C55E]/30 bg-[#22C55E]/10 text-[#22C55E] text-xs uppercase tracking-widest px-3 py-2.5 rounded-sm font-mono-d" data-testid="broadcast-last-result">
            Last send → audience: {lastResult.audience} · sent {lastResult.sent} · skipped {lastResult.skipped} · candidates {lastResult.candidates}
          </div>
        )}
      </div>

      <div className="mt-8 text-[10px] uppercase tracking-[0.3em] text-zinc-600">
        Powered by IONOS SMTP · From info@accuratemonkey.com
      </div>
    </div>
  );
}
